import { formatCopy, namespaceCopyFor } from '@/i18n/ns';
import workspaceFeaturesCopy from '@/i18n/ns/workspace-features';
import { localeLanguage } from '@/i18n/eu-locales';
import type { WorkspaceIconName } from '../icons';
import type {
  ProjectItem,
  ProjectPerson,
  ProjectRole,
  ProjectSummary,
  ProjectVisibility,
} from './types';

export const WHERE_ICON: Record<ProjectVisibility, WorkspaceIconName> = {
  private: 'lock',
  members: 'users',
  company: 'globe',
};

type ProjectCopy = typeof workspaceFeaturesCopy.en;
function projectCopy(locale?: string): ProjectCopy {
  return namespaceCopyFor(workspaceFeaturesCopy, localeLanguage(locale));
}

/** Where something lives, in Recent chats' words. */
export function whereLabel(visibility: ProjectVisibility, companyName: string, locale?: string) {
  const copy = projectCopy(locale);
  return visibility === 'company'
    ? formatCopy(copy.visibilityCompany, { company: companyName })
    : visibility === 'members'
      ? copy.visibilityMembers
      : copy.visibilityPrivate;
}

export function roleLabel(role: ProjectRole, locale?: string) {
  const copy = projectCopy(locale);
  return role === 'owner' ? copy.roleLead : role === 'editor' ? copy.roleCanEdit : copy.roleCanUse;
}

export function firstName(name: string) {
  return name.trim().split(' ')[0] || name;
}

/** A person's name as shown: their name, or "A former colleague" for a deleted account. */
export function personName(person: Pick<ProjectPerson, 'userId' | 'name'>, locale?: string) {
  return person.userId ? person.name : projectCopy(locale).formerColleague;
}

export const isViewer = (
  person: Pick<ProjectPerson, 'userId'>,
  viewerId: string | null | undefined,
) => Boolean(person.userId && person.userId === viewerId);

/**
 * Other people as the subject of a sentence template's `{key}`: first names joined the language's
 * way, and a deleted account as "a former colleague", capitalised only where the sentence starts
 * with it. You are never put into another person's sentence: you have sentences of your own
 * (the *You keys), so each language can conjugate them.
 */
export function peopleIn(template: string, key: string, people: ProjectPerson[], locale?: string) {
  const atStart = template.trimStart().startsWith(`{${key}}`);
  const former = projectCopy(locale).formerColleague;
  return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(
    people.map((person, index) =>
      person.userId
        ? firstName(person.name)
        : index === 0 && atStart
          ? former
          : former.charAt(0).toLocaleLowerCase(locale) + former.slice(1),
    ),
  );
}

const RESULT_KINDS: Record<string, { key: keyof ProjectCopy; icon: WorkspaceIconName }> = {
  app: { key: 'kindApp', icon: 'app' },
  deck: { key: 'kindDeck', icon: 'slides' },
  slides: { key: 'kindDeck', icon: 'slides' },
  table: { key: 'kindSheet', icon: 'table' },
  spreadsheet: { key: 'kindSheet', icon: 'table' },
  chart: { key: 'kindChart', icon: 'results' },
  dashboard: { key: 'kindDashboard', icon: 'dashboard' },
  report: { key: 'kindReport', icon: 'report' },
  kpi: { key: 'kindDashboard', icon: 'dashboard' },
};

/** The kind word and icon a row shows. */
export function itemKind(
  item: Pick<ProjectItem, 'kind' | 'resultKind'>,
  locale?: string,
): {
  label: string;
  icon: WorkspaceIconName;
} {
  const copy = projectCopy(locale);
  if (item.kind === 'chat') return { label: copy.kindChat, icon: 'chat' };
  if (item.kind === 'file') return { label: copy.kindFile, icon: 'file' };
  if (item.kind === 'meeting') return { label: copy.kindMeeting, icon: 'meetings' };
  if (item.kind === 'agent') return { label: copy.kindAgent, icon: 'agents' };
  const result = RESULT_KINDS[item.resultKind ?? ''];
  return result
    ? { label: copy[result.key], icon: result.icon }
    : { label: copy.kindDocument, icon: 'document' };
}

/** "Now", "11:42", "Yesterday", "Mon", "Oct 9": the same short dates Recent chats uses. Inside a
 * sentence (`inline`), yesterday is the language's own word for it there ("changed by Elias,
 * yesterday"). */
export function shortWhen(value: string, locale?: string, now = new Date(), inline = false) {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((day(now) - day(date)) / 86_400_000);
  if (days <= 0)
    return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(date);
  if (days === 1) {
    const word = projectCopy(locale).yesterday;
    if (!inline) return word;
    // Not every browser has every language's word (Chromium lacks Irish and Maltese): then the
    // catalog's, lowercased for the middle of a sentence.
    return Intl.RelativeTimeFormat.supportedLocalesOf(locale ?? 'en').length
      ? new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(-1, 'day')
      : word.toLocaleLowerCase(locale);
  }
  if (days < 7) return new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date);
  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(date);
}

/** "Ends 30 November", or "Ended 31 August" once it has passed. */
export function endsLabel(endsOn: string, finished: boolean, locale?: string) {
  const date = new Date(`${endsOn}T12:00:00`);
  if (!Number.isFinite(date.getTime())) return '';
  const copy = projectCopy(locale);
  const text = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }).format(date);
  return `${finished ? copy.ended : copy.ends} ${text}`;
}

/** What a row says under its title: "Sheet · You · made in a chat here". */
export function itemLine(item: ProjectItem, viewerId: string | null | undefined, locale?: string) {
  const copy = projectCopy(locale);
  const yours = item.own || item.owner.userId === viewerId;
  if (item.kind === 'chat' && item.live)
    return `${copy.kindChat} · ${
      yours
        ? copy.gettingAnswer
        : formatCopy(copy.gettingAnswerOther, {
            name: peopleIn(copy.gettingAnswerOther, 'name', [item.owner], locale),
          })
    }`;
  const parts = [itemKind(item, locale).label, yours ? copy.you : personName(item.owner, locale)];
  if (item.meta) parts.push(item.meta);
  if (item.via === 'made') parts.push(copy.madeInChatHere);
  const by = item.via === 'added' ? item.addedBy : undefined;
  if (by && by.userId !== item.owner.userId)
    parts.push(
      isViewer(by, viewerId)
        ? copy.addedByYou
        : by.userId
          ? formatCopy(copy.addedByOther, { name: firstName(by.name) })
          : copy.addedByFormer,
    );
  return parts.join(' · ');
}

/** When something last happened in a project: its newest item, or the project itself. */
export function lastActive(project: ProjectSummary) {
  const latest = project.latest?.at;
  return latest && latest > project.updatedAt ? latest : project.updatedAt;
}

const LATEST_EVENTS: Record<
  NonNullable<ProjectSummary['latest']>['event'],
  { you: keyof ProjectCopy; other: keyof ProjectCopy }
> = {
  started: { you: 'latestStartedYou', other: 'latestStarted' },
  continued: { you: 'latestContinuedYou', other: 'latestContinued' },
  made: { you: 'latestMadeYou', other: 'latestMade' },
  added: { you: 'latestAddedYou', other: 'latestAdded' },
};

/** The newest thing in a project, in a few words: "Elias added Supplier terms 2027.pdf". */
export function latestLine(
  latest: NonNullable<ProjectSummary['latest']>,
  viewerId: string | null | undefined,
  locale?: string,
) {
  const copy = projectCopy(locale);
  const you = isViewer(latest.actor, viewerId);
  if (latest.kind === 'chat' && latest.live)
    return you
      ? copy.latestAnswerYou
      : formatCopy(copy.latestAnswerOther, {
          name: peopleIn(copy.latestAnswerOther, 'name', [latest.actor], locale),
        });
  const event = LATEST_EVENTS[latest.event] ?? LATEST_EVENTS.added;
  if (you) return formatCopy(copy[event.you], { title: latest.title });
  const template = copy[event.other];
  return formatCopy(template, {
    name: peopleIn(template, 'name', [latest.actor], locale),
    title: latest.title,
  });
}
